import { databases } from "@/lib/appwrite";
import { isCertificateApproved } from "@/requests/certificates/employmentCertificate/views/config/certificateApprovals";

interface UpdateCertificateByManagerParams {
    requestId: string;
    action: 'approve' | 'reject';
    managerUserId: string;
    rejectionReason?: string;
    certificateType: string;
    currentStatusByRrhh: string;
    currentStatusBySupervisor: string;
}

export const updateCertificateRequestByManager = async ({
    requestId,
    action,
    managerUserId,
    rejectionReason,
    certificateType,
    currentStatusByRrhh,
    currentStatusBySupervisor
}: UpdateCertificateByManagerParams) => {
    try {
        const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID;
        const collectionId = import.meta.env.VITE_CERTIFICATE_REQUESTS_COLLECTION_ID;

        if (!databaseId || !collectionId) {
            throw new Error("Missing environment variables for database or collection");
        }

        // Preparar los datos base de actualización
        const updateData: any = {};

        if (action === 'approve') {
            // APROBACIÓN POR MANAGER
            updateData.statusByManager = 'approved';
            updateData.idApprovedByManager = managerUserId;
            
            // Verificar si este certificado ya está completamente aprobado
            // Simular el nuevo estado con Manager aprobado
            const isFullyApproved = isCertificateApproved(
                certificateType,
                currentStatusByRrhh,
                currentStatusBySupervisor,
                'approved' // Manager está aprobando ahora
            );

            if (isFullyApproved) {
                // Si ya tiene todas las aprobaciones requeridas, actualizar status y approvedAt
                // Como Manager es el de mayor jerarquía, cuando aprueba el certificado queda aprobado
                updateData.status = 'approved';
                updateData.approvedAt = new Date().toISOString();
            } else {
                // Si aún faltan aprobaciones, mantener status como pending
                updateData.status = 'pending';
            }

        } else if (action === 'reject') {
            // RECHAZO POR MANAGER
            if (!rejectionReason || rejectionReason.trim() === '') {
                throw new Error("El motivo del rechazo es obligatorio");
            }

            updateData.statusByManager = 'rejected';
            updateData.commentsManager = rejectionReason;
            updateData.idApprovedByManager = managerUserId; // Registrar quién rechazó
            
            // Si Manager rechaza, el status general es rechazado
            // Manager tiene autoridad final
            updateData.status = 'rejected';
        }

        // Ejecutar la actualización
        const response = await databases.updateDocument(
            databaseId,
            collectionId,
            requestId,
            updateData
        );

        return {
            success: true,
            data: response,
            message: action === 'approve' 
                ? 'Solicitud aprobada exitosamente' 
                : 'Solicitud rechazada exitosamente'
        };

    } catch (error) {
        console.error("Error updating certificate request by Manager:", error);
        return {
            success: false,
            error: error instanceof Error ? error.message : "Error desconocido al actualizar la solicitud"
        };
    }
};

// Función auxiliar para obtener el estado actualizado de una solicitud
export const getUpdatedCertificateRequest = async (requestId: string) => {
    try {
        const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID;
        const collectionId = import.meta.env.VITE_CERTIFICATE_REQUESTS_COLLECTION_ID;

        const response = await databases.getDocument(
            databaseId,
            collectionId,
            requestId
        );

        return {
            success: true,
            data: response
        };
    } catch (error) {
        console.error("Error getting updated certificate request:", error);
        return {
            success: false,
            error: error instanceof Error ? error.message : "Error al obtener la solicitud"
        };
    }
};